import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AppContext } from "../../context/AppContext";
import ModalLoading from "../ModalLoading";
import SimpleAlert from "../SimpleAlert";
import CopmanyDetailsStyles from "../../css/CompanyDetailsStyles.module.css";
import { validateIco } from "../../utils/axios_functions/validateIco";
import formatCurrency from "../../utils/functions/formatCurrency";
import billingStyles from "../../css/heatmaps/BillingDetailsForm.module.css";
import ButtonBtn from "../buttons/ButtonBtn";
import heatmapsLogo from "../../img/logos/heatmaps_logo_left_inverse.svg";

const countryIcoLabels = {
  cs: "IČO",
  sk: "IČO",
  hu: "IČO (Adószám)",
  pl: "IČO (NIP)",
  ro: "IČO (CUI)",
  de: "IČO (USt-IdNr.)",
};

const countryIcoFormats = {
  cs: /^\d{8}$/,
  sk: /^\d{8}$/,
  hu: /^\d{8}$/,
  pl: /^\d{10}$/,
  ro: /^\d{2,10}$/,
  de: /^\d{9}$/,
};

const BillingDetailsForm = ({ selectedPlan, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const { lng } = useParams();
  const {
    isAlertVisible,
    showAlert,
    closeAlert,
    alertMessage,
    setIsUserDataLoading,
    isUserDataLoading,
    userData,
  } = useContext(AppContext);

  const [customerType, setCustomerType] = useState("b2c"); // b2c | b2b
  const [isCountrySelectOpen, setIsCountrySelectOpen] = useState(false);
  const [showIcoTooltip, setShowIcoTooltip] = useState(false);
  const [companyInfoFromIco, setCompanyInfoFromIco] = useState(null);

  const [formData, setFormData] = useState({
    // shared
    email: "",
    phone: "",
    note: "",
    saveForNext: true,
    tos: false,
    gdpr: false,

    // country selection for ICO
    country_billing: lng === "en" ? "cs" : lng || "cs",

    // b2c fields
    first_name: "",
    last_name: "",

    // b2b fields
    name: "",
    street: "",
    city: "",
    postal_code: "",
    state: "",
    ico: "",
    dic: "",
  });

  const [errors, setErrors] = useState({});
  const [prefilledOnce, setPrefilledOnce] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);

  // Country select options (show full names, store ISO alpha-2)
  const COUNTRY_OPTIONS = [
    { code: "CZ", label: "Česko" },
    { code: "SK", label: "Slovensko" },
    { code: "HU", label: "Maďarsko" },
    { code: "PL", label: "Polsko" },
    { code: "RO", label: "Rumunsko" },
    { code: "DE", label: "Německo" },
  ];

  const mapToIsoFromLang = (code) => {
    const lc = String(code || "").toLowerCase();
    const m = { cs: "CZ", sk: "SK", hu: "HU", pl: "PL", ro: "RO", de: "DE" };
    return m[lc] || undefined;
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isCountrySelectOpen) {
      const handleKeyEvent = (e) => {
        if (e.key === "Escape") {
          setIsCountrySelectOpen(false);
        }
      };
      const handleClickOutside = (e) => {
        if (!e.target.closest(".country")) {
          setIsCountrySelectOpen(false);
        }
      };
      window.addEventListener("keydown", handleKeyEvent);
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        window.removeEventListener("keydown", handleKeyEvent);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isCountrySelectOpen]);

  const handleCountryChange = (lang) => {
    setFormData((prev) => ({
      ...prev,
      country_billing: lang,
      // reset company fields on country change to avoid invalid ICO
      name: "",
      street: "",
      city: "",
      postal_code: "",
      ico: "",
      dic: "",
      state: "",
    }));
    setIsCountrySelectOpen(false);
  };

  const handleInputChange = async (e) => {
    const { id, value } = e.target;
    if (!hasUserEdited) setHasUserEdited(true);

    if (id === "postal_code") {
      const numericValue = value.replace(/[^0-9]/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [id]: numericValue }));
      setErrors((prev) => ({ ...prev, [id]: numericValue === "" }));
      return;
    }

    if (id === "ico") {
      const sanitizedValue = value.replace(/\s+/g, "");
      if (
        sanitizedValue.length === 8 &&
        (formData.country_billing === "cs" || formData.country_billing === "sk")
      ) {
        try {
          const response = await validateIco(
            sanitizedValue,
            formData.country_billing
          );
          if (response.status === 200) {
            setShowIcoTooltip(true);
            setCompanyInfoFromIco(response.data);
          } else {
            showAlert?.(
              t("companyDetailsForm.errorMessages.icoNotFoundErrorMsg"),
              "danger"
            );
          }
        } catch (err) {
          showAlert?.(
            t("companyDetailsForm.errorMessages.icoNotFoundErrorMsg"),
            "danger"
          );
        }
      } else {
        setShowIcoTooltip(false);
      }
      setFormData((prev) => ({ ...prev, [id]: sanitizedValue }));
      setErrors((prev) => ({ ...prev, [id]: sanitizedValue.trim() === "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [id]: value }));
    // Update error only for required/validated fields; leave optional ones untouched
    const checkable = [
      "first_name",
      "last_name",
      "name",
      "street",
      "city",
      "postal_code",
      "ico",
      "email",
      "phone",
    ];
    if (checkable.includes(id)) {
      setErrors((prev) => ({ ...prev, [id]: value.trim?.() === "" }));
    }
  };

  // Prefill from userData once (do not mutate context; do not overwrite user's edits)
  useEffect(() => {
    if (prefilledOnce || !userData || hasUserEdited) return;
    try {
      const next = { ...formData };
      // Shared
      next.email = userData.email ?? next.email;
      next.phone = userData.phone ?? next.phone;
      // Determine type
      const hasCompany = Boolean(
        userData.company_name || userData.company || userData.ico
      );
      const initialType = hasCompany ? "b2b" : "b2c";
      setCustomerType(initialType);
      // Country
      next.country_billing =
        userData.country_billing || userData.country || next.country_billing;
      // B2C
      next.first_name = userData.first_name ?? next.first_name;
      next.last_name = userData.last_name ?? next.last_name;
      // B2B
      next.name = userData.company_name || userData.company || next.name;
      next.street = userData.street || userData.billing_street || next.street;
      next.city = userData.city || userData.billing_city || next.city;
      next.postal_code =
        userData.postal_code ||
        userData.billing_postal_code ||
        next.postal_code;
      next.state = userData.state || userData.billing_state || next.state;
      next.ico = userData.ico || next.ico;
      next.dic = userData.dic || userData.vat_id || userData.vat || next.dic;
      setFormData(next);
    } catch (_) {
      // ignore
    } finally {
      setPrefilledOnce(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, prefilledOnce, hasUserEdited]);

  const validateFields = () => {
    const newErrors = {};
    const zipPattern = /^[0-9]+$/;
    const emailPattern = /.+@.+\..+/;
    const phoneMinDigits = 6; // basic sanity check across countries

    // required
    if (!formData.email || !emailPattern.test(formData.email))
      newErrors.email = true;
    const digitsOnlyPhone = String(formData.phone || "").replace(/\D/g, "");
    if (!digitsOnlyPhone || digitsOnlyPhone.length < phoneMinDigits)
      newErrors.phone = true;

    if (customerType === "b2c") {
      if (!formData.first_name) newErrors.first_name = true;
      if (!formData.last_name) newErrors.last_name = true;
    } else {
      if (!formData.name) newErrors.name = true;
      if (!formData.street) newErrors.street = true;
      if (!formData.city) newErrors.city = true;
      if (!formData.postal_code || !zipPattern.test(formData.postal_code))
        newErrors.postal_code = true;
      if (
        !formData.ico ||
        !countryIcoFormats[formData.country_billing].test(formData.ico)
      )
        newErrors.ico = true;
      // DIČ/VAT optional
    }

    // legal consents
    if (!formData.gdpr) newErrors.gdpr = true;

    return newErrors;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const newErrors = validateFields();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      const keys = Object.keys(newErrors);
      if (keys.length === 1 && keys[0] === "gdpr") {
        // All fields valid except GDPR consent
        showAlert?.(
          t("Je potřeba dát souhlas se zpracováním osobních údajů."),
          "danger"
        );
      } else {
        showAlert?.(
          t("companyDetailsForm.errorMessages.fillAllFieldsErrorMsg"),
          "danger"
        );
      }
      return;
    }

    try {
      setIsUserDataLoading?.(true);
      const payload = { customerType, ...formData, selectedPlan };
      await onSubmit?.(payload);
    } finally {
      setIsUserDataLoading?.(false);
    }
  };

  // Show validation as input placeholders to avoid layout shifts
  const getErrorPlaceholder = (id) => {
    const val = String(formData[id] ?? "").trim();
    switch (id) {
      case "ico":
        return val === ""
          ? t("companyDetailsForm.required")
          : t(`companyDetailsForm.requiredId.${formData.country_billing}`);
      case "city":
        return val === ""
          ? t("companyDetailsForm.required")
          : t("companyDetailsForm.requiredLetters");
      case "postal_code":
        return val === ""
          ? t("companyDetailsForm.required")
          : t("companyDetailsForm.requiredNumbers");
      case "email":
        return val === ""
          ? t("companyDetailsForm.required")
          : t("companyDetailsForm.required");
      case "phone":
        return t("companyDetailsForm.required");
      default:
        return t("companyDetailsForm.required");
    }
  };

  return (
    <section className="section register" style={{ paddingTop: 0 }}>
      {isAlertVisible && (
        <SimpleAlert
          message={alertMessage?.text}
          onClose={closeAlert}
          type={alertMessage?.type}
        />
      )}
      {isUserDataLoading && (
        <ModalLoading
          message={t(
            "companyDetailsForm.loadingMessages.searchingForCompanyLoadMsg"
          )}
        />
      )}
      <article className="container">
        <form
          className={`form ${billingStyles.root}`}
          onSubmit={submit}
          autoComplete="off"
        >
          {/* Typ zákazníka */}
          <div className="box">
            {/* Selected plan summary */}
            {selectedPlan && (
              <div className={`form__column ${billingStyles.summary}`}>
                <div className={billingStyles.summaryLeft}>
                  <p>
                    {t("Vybraný plán")}: <strong>{selectedPlan.name}</strong>
                  </p>
                  <p>
                    {t("Cena")}:{" "}
                    <strong>
                      {formatCurrency(
                        selectedPlan?.price || 0,
                        selectedPlan.currency,
                        selectedPlan?.has_discount ? 2 : 0,
                        selectedPlan?.has_discount ? 2 : 0
                      )}
                    </strong>
                    {selectedPlan?.has_discount &&
                      selectedPlan?.original_price && (
                        <span
                          style={{
                            marginLeft: "8px",
                            textDecoration: "line-through",
                            opacity: 0.6,
                            fontSize: "0.9em",
                          }}
                        >
                          {formatCurrency(
                            selectedPlan.original_price,
                            selectedPlan.currency,
                            0,
                            0
                          )}
                        </span>
                      )}
                  </p>
                </div>
                <div className={billingStyles.summaryRight}>
                  <img
                    src={heatmapsLogo}
                    alt="Permoniq Heatmaps"
                    className={billingStyles.logo}
                  />
                </div>
              </div>
            )}
            <div className={`form__column ${billingStyles.row}`}>
              <p>
                <label>{t("Typ zákazníka")}</label>
                <span style={{ display: "flex", gap: 16 }}>
                  <label
                    className="checkbox"
                    htmlFor="customerType_b2c"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      id="customerType_b2c"
                      name="customerType"
                      checked={customerType === "b2c"}
                      onChange={() => setCustomerType("b2c")}
                    />
                    <span className="checkbox__label">
                      {t("Soukromá osoba")}
                    </span>
                    <span
                      className="checkbox__checker"
                      style={{ borderRadius: "50%" }}
                    ></span>
                  </label>
                  <label
                    className="checkbox"
                    htmlFor="customerType_b2b"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      id="customerType_b2b"
                      name="customerType"
                      checked={customerType === "b2b"}
                      onChange={() => setCustomerType("b2b")}
                    />
                    <span className="checkbox__label">{t("Firma")}</span>
                    <span
                      className="checkbox__checker"
                      style={{ borderRadius: "50%" }}
                    ></span>
                  </label>
                </span>
              </p>
              {customerType === "b2b" ? (
                <p style={{ position: "relative" }}>
                  <label htmlFor="ico">
                    {countryIcoLabels[formData.country_billing] ||
                      t("companyDetailsForm.id")}
                    <span className={billingStyles.required}>*</span>
                  </label>
                  <span
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: "100%",
                    }}
                  >
                    <input
                      type="text"
                      id="ico"
                      value={formData.ico}
                      onChange={handleInputChange}
                      className={
                        errors.ico ? billingStyles.inputError : undefined
                      }
                      placeholder={
                        errors.ico ? getErrorPlaceholder("ico") : undefined
                      }
                    />
                    {showIcoTooltip && (
                      <span>
                        <span
                          className={`${CopmanyDetailsStyles.ico_company_tooltip}`}
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              name: companyInfoFromIco?.company_name || "",
                              street: companyInfoFromIco?.street || "",
                              city: companyInfoFromIco?.city || "",
                              postal_code:
                                companyInfoFromIco?.postal_code || "",
                            }));
                            setShowIcoTooltip(false);
                          }}
                        >
                          {companyInfoFromIco?.company_name !== undefined
                            ? `${companyInfoFromIco?.company_name} ${companyInfoFromIco?.ico}`
                            : t(
                                "companyDetailsForm.errorMessages.icoNotFoundErrorMsg"
                              )}
                        </span>
                        <span
                          className={`${CopmanyDetailsStyles.ico_tooltip_close_btn} "sibling"`}
                          onClick={() => setShowIcoTooltip(false)}
                        >
                          &times;
                        </span>
                      </span>
                    )}
                  </span>
                  {/* error shown as placeholder */}
                </p>
              ) : (
                <p className={billingStyles.spacer} aria-hidden="true"></p>
              )}
            </div>

            {/* B2C fields */}
            {customerType === "b2c" && (
              <>
                <div className={`form__column ${billingStyles.row}`}>
                  <p>
                    <label htmlFor="first_name">
                      {t("Jméno")}
                      <span className={billingStyles.required}>*</span>
                    </label>
                    <input
                      id="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className={
                        errors.first_name ? billingStyles.inputError : undefined
                      }
                      placeholder={
                        errors.first_name
                          ? getErrorPlaceholder("first_name")
                          : undefined
                      }
                    />
                  </p>
                  <p>
                    <label htmlFor="last_name">
                      {t("Příjmení")}
                      <span className={billingStyles.required}>*</span>
                    </label>
                    <input
                      id="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className={
                        errors.last_name ? billingStyles.inputError : undefined
                      }
                      placeholder={
                        errors.last_name
                          ? getErrorPlaceholder("last_name")
                          : undefined
                      }
                    />
                  </p>
                </div>
              </>
            )}

            {/* B2B fields */}
            {customerType === "b2b" && (
              <>
                {/* IČO je nyní umístěno vedle výběru typu zákazníka výše */}

                <div className={`form__column ${billingStyles.row}`}>
                  <p>
                    <label htmlFor="name">
                      {t("companyDetailsForm.company")}
                      <span className={billingStyles.required}>*</span>
                    </label>
                    <input
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={
                        errors.name ? billingStyles.inputError : undefined
                      }
                      placeholder={
                        errors.name ? getErrorPlaceholder("name") : undefined
                      }
                    />
                  </p>
                  <p>
                    <label htmlFor="street">
                      {t("companyDetailsForm.street")}
                      <span className={billingStyles.required}>*</span>
                    </label>
                    <input
                      id="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      className={
                        errors.street ? billingStyles.inputError : undefined
                      }
                      placeholder={
                        errors.street
                          ? getErrorPlaceholder("street")
                          : undefined
                      }
                    />
                  </p>
                </div>

                <div className={`form__column ${billingStyles.row}`}>
                  <p>
                    <label htmlFor="city">
                      {t("companyDetailsForm.city")}
                      <span className={billingStyles.required}>*</span>
                    </label>
                    <input
                      id="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={
                        errors.city ? billingStyles.inputError : undefined
                      }
                      placeholder={
                        errors.city ? getErrorPlaceholder("city") : undefined
                      }
                    />
                  </p>
                  <p>
                    <label htmlFor="postal_code">
                      {t("companyDetailsForm.zip")}
                      <span className={billingStyles.required}>*</span>
                    </label>
                    <input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      className={
                        errors.postal_code
                          ? billingStyles.inputError
                          : undefined
                      }
                      placeholder={
                        errors.postal_code
                          ? getErrorPlaceholder("postal_code")
                          : undefined
                      }
                    />
                  </p>
                </div>

                <div className={`form__column ${billingStyles.row}`}>
                  <p>
                    <label htmlFor="dic">{t("companyDetailsForm.taxid")}</label>
                    <input
                      id="dic"
                      value={formData.dic}
                      onChange={handleInputChange}
                    />
                  </p>
                  <p>
                    <label htmlFor="state">{t("Stát")}</label>
                    <select
                      id="state"
                      value={
                        formData.state ||
                        mapToIsoFromLang(formData.country_billing) ||
                        ""
                      }
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          state: e.target.value,
                        }))
                      }
                    >
                      <option value="" disabled>
                        {t("Vyberte stát")}
                      </option>
                      {COUNTRY_OPTIONS.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </p>
                </div>
              </>
            )}

            {/* Shared contact fields */}
            <div className={`form__column ${billingStyles.row}`}>
              <p>
                <label htmlFor="email">
                  {t("companyDetailsForm.email")}
                  <span className={billingStyles.required}>*</span>
                </label>
                <input
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={
                    errors.email ? billingStyles.inputError : undefined
                  }
                  placeholder={
                    errors.email ? getErrorPlaceholder("email") : undefined
                  }
                />
              </p>
              <p>
                <label htmlFor="phone">
                  {t("Telefon")}
                  <span className={billingStyles.required}>*</span>
                </label>
                <input
                  id="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={
                    errors.phone ? billingStyles.inputError : undefined
                  }
                  placeholder={
                    errors.phone ? getErrorPlaceholder("phone") : undefined
                  }
                />
              </p>
            </div>

            {/* Optional */}
            <div className="form__column">
              {/*
              <p>
                <label htmlFor="note">{t("Poznámka k faktuře")}</label>
                <textarea id="note" value={formData.note} onChange={handleInputChange} rows={3} />
              </p>
              <p>
                <label className="checkbox" htmlFor="saveForNext_billing">
                  <input
                    type="checkbox"
                    name="saveForNext_billing"
                    id="saveForNext_billing"
                    checked={formData.saveForNext}
                    onChange={(e) => setFormData((prev) => ({ ...prev, saveForNext: e.target.checked }))}
                  />
                  <span className="checkbox__label">{t("Uložit údaje pro příští nákup")}</span>
                  <span className="checkbox__checker"></span>
                </label>
              </p>
              */}
            </div>

            {/* Legal */}
            <div className={`form__column ${billingStyles.legal}`}>
              <p>
                <label className="checkbox" htmlFor="gdpr_billing">
                  <input
                    type="checkbox"
                    name="gdpr_billing"
                    id="gdpr_billing"
                    checked={formData.gdpr}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        gdpr: e.target.checked,
                      }))
                    }
                  />
                  <span className="checkbox__label">
                    {t("Souhlasím se zpracováním osobních údajů")}
                  </span>
                  <span className="checkbox__checker"></span>
                </label>
              </p>
              {/* No textual error under GDPR; validation still enforced on submit */}
            </div>

            <div className={billingStyles.requiredNote}>
              <span className={billingStyles.required}>*</span>
              <em style={{ color: "black", fontSize: "1rem" }}>
                {t("requireItem")}
              </em>
            </div>
          </div>

          <div className={`register__submit ${billingStyles.actions}`}>
            <ButtonBtn type="submit" marginBlock={0} justifyContent="flex-end">
              {t("companyDetailsForm.submit")}
            </ButtonBtn>
            <ButtonBtn
              type="button"
              onClick={onCancel}
              marginBlock={0}
              className="btn--secondary"
              justifyContent="flex-start"
            >
              {t("Zpět")}
            </ButtonBtn>
          </div>
        </form>
      </article>
    </section>
  );
};

export default BillingDetailsForm;
